import { useEffect, useRef, useState } from 'react'
import { X, Send, Loader2 } from 'lucide-react'
import { useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'
import { useFeedbackStore } from './feedbackStore'
import { useFeedbackSubmit } from './useFeedbackSubmit'
import {
  confirmResolution,
  fetchKnownIssues,
  fetchMyReports,
  type MyFeedbackReport,
} from './api'
import { collectAttachedState } from './attachedStateCollector'
import { toast } from '@/stores/toast'
import { cn } from '@/utils/cn'

/**
 * The in-app feedback widget. Mounted once near the AppShell root; opens in
 * response to the Zustand `feedbackStore` (any caller can `useFeedbackStore
 * .getState().open()`).
 *
 * Deliberate UX choices:
 *
 *   - Single textarea, no category picker, no severity picker, no subject —
 *     the user's job is to type words, everything else is the system's. The
 *     ONE exception (CLA-883 audit R2) is the patient-impact toggle: urgency
 *     is something only the reporter knows, and it must not wait on AI triage.
 *   - The confirmation sets an expectation (audit R1) — users tolerate slow
 *     fixes far better than silence; what they can't forgive is not knowing
 *     whether anyone heard them.
 *   - "Your reports" (audit R3) gives pull-based status visibility: plain
 *     language states, never the ops vocabulary.
 *   - Page indicator at the bottom shows where the system thinks the user is
 *     (route + parsed params) so there's no surprise about what gets
 *     attached.
 *   - ⌘⏎ / Ctrl+⏎ to submit, Esc to cancel. ⏎ alone adds newlines so the
 *     user can paragraph their feedback.
 *
 * Split into an outer gate + inner panel so the panel's `useState(prefill)`
 * initialises fresh on each open (no `setState` inside `useEffect` —
 * forbidden by React Compiler lint rules in this codebase).
 */
export function FeedbackModal() {
  const isOpen = useFeedbackStore((s) => s.isOpen)
  if (!isOpen) return null
  return <FeedbackPanel />
}

function FeedbackPanel() {
  const { entryPoint, prefill, sentryEventId, close } = useFeedbackStore()
  const [view, setView] = useState<'compose' | 'reports'>('compose')
  const [message, setMessage] = useState(prefill ?? '')
  const [patientImpact, setPatientImpact] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const submit = useFeedbackSubmit()

  // Focus the textarea on mount.
  useEffect(() => {
    const t = setTimeout(() => textareaRef.current?.focus(), 30)
    return () => clearTimeout(t)
  }, [])

  const attached = collectAttachedState()
  const pageLabel = formatPageLabel(attached.route, attached.route_params)
  const canSubmit = message.trim().length > 0 && !submit.isPending

  function handleSubmit() {
    if (!canSubmit) return
    submit.mutate(
      {
        message: message.trim(),
        platform: 'web',
        entry_point: entryPoint,
        source_url: attached.route,
        attached_state: attached,
        app_version: import.meta.env.VITE_RELEASE,
        sentry_event_id: sentryEventId ?? undefined,
        patient_impact: patientImpact || undefined,
      },
      {
        onSuccess: (resp) => {
          // R1: an acknowledgement with an expectation, not just a receipt.
          toast.success(
            patientImpact
              ? `${resp.reference} flagged urgent — the team has been pinged and will respond as soon as possible.`
              : `Thanks — ${resp.reference} logged. We usually reply within a few business hours; track it under "Your reports".`,
          )
          close()
        },
        // Errors surface via the global mutationCache handler in main.tsx.
        onError: () => close(),
      },
    )
  }

  function handleKey(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      e.preventDefault()
      close()
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      handleSubmit()
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Send feedback"
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] bg-black/40"
      onClick={close}
    >
      <div
        className="w-full max-w-xl mx-4 bg-surface border border-border-subtle rounded-xl shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setView('compose')}
              className={cn(
                'text-sm font-medium transition-colors',
                view === 'compose' ? 'text-ink' : 'text-ink-faint hover:text-ink',
              )}
            >
              Send feedback
            </button>
            <button
              type="button"
              onClick={() => setView('reports')}
              className={cn(
                'text-sm font-medium transition-colors',
                view === 'reports' ? 'text-ink' : 'text-ink-faint hover:text-ink',
              )}
            >
              Your reports
            </button>
          </div>
          <button
            type="button"
            onClick={close}
            className="text-ink-faint hover:text-ink transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {view === 'reports' ? (
          <MyReports onClose={close} />
        ) : (
          <>
            <KnownIssuesStrip />
            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKey}
              placeholder="What's on your mind? Bug, friction, idea — anything."
              rows={6}
              className="w-full px-4 py-3 text-sm bg-transparent text-ink placeholder:text-ink-faint resize-none focus:outline-none"
              aria-label="Feedback message"
            />

            <label className="flex items-center gap-2 px-4 pb-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={patientImpact}
                onChange={(e) => setPatientImpact(e.target.checked)}
                className="accent-error"
              />
              <span
                className={cn(
                  'text-xs',
                  patientImpact ? 'text-on-error-soft font-medium' : 'text-ink-tertiary',
                )}
              >
                This is stopping me treating a patient right now
              </span>
            </label>

            <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-t border-border-subtle bg-surface-secondary">
              <p className="text-xs text-ink-faint truncate" title={pageLabel}>
                <span className="text-ink-tertiary">Page:</span> {pageLabel}
              </p>
              <div className="flex items-center gap-2 shrink-0">
                <kbd className="px-1.5 py-0.5 text-[10px] font-mono text-ink-faint border border-border-subtle rounded">
                  ⌘⏎
                </kbd>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors',
                    canSubmit
                      ? 'bg-claro-600 text-white hover:bg-claro-700'
                      : 'bg-surface-tertiary text-ink-disabled cursor-not-allowed',
                  )}
                >
                  {submit.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Send className="w-3.5 h-3.5" />
                  )}
                  Send
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

const STATUS_COPY: Record<MyFeedbackReport['status_label'], { label: string; tone: string }> = {
  received: { label: "We've got it", tone: 'bg-surface-tertiary text-ink-secondary' },
  in_progress: { label: 'Looking into it', tone: 'bg-info-light text-on-info-soft' },
  waiting_on_you: { label: 'Waiting on you', tone: 'bg-warning-light text-on-warning-soft' },
  on_our_list: { label: 'On our list', tone: 'bg-surface-tertiary text-ink-secondary' },
  fixed: { label: 'Fixed', tone: 'bg-success-light text-on-success-soft' },
  closed: { label: 'Closed', tone: 'bg-surface-tertiary text-ink-tertiary' },
}

/**
 * Known issues we're already on (audit R5) — shown before the user types so
 * they don't burn minutes reporting something we know about.
 */
function KnownIssuesStrip() {
  const { data } = useQuery({
    queryKey: ['feedback', 'known-issues'],
    queryFn: fetchKnownIssues,
    staleTime: 60_000,
  })

  const issues = data?.known_issues ?? []
  if (issues.length === 0) return null

  return (
    <div className="px-4 pt-3">
      <p className="text-[11px] font-medium text-on-warning-soft mb-1">
        Known issues we're already on:
      </p>
      {issues.slice(0, 3).map((k) => (
        <p key={k.id} className="text-xs text-ink-secondary" title={k.body ?? undefined}>
          • {k.title}
          {k.body ? <span className="text-ink-faint"> — {k.body}</span> : null}
        </p>
      ))}
    </div>
  )
}

/** Pull-based status: the place to check, so we never have to spam. */
function MyReports({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['feedback', 'mine'],
    queryFn: fetchMyReports,
  })

  async function answer(id: number, sorted: boolean) {
    await confirmResolution(id, sorted)
    qc.invalidateQueries({ queryKey: ['feedback', 'mine'] })
  }

  const reports = data?.reports ?? []

  return (
    <div className="max-h-96 overflow-y-auto">
      {isLoading && <p className="px-4 py-6 text-sm text-ink-faint">Loading…</p>}
      {isError && (
        <p className="px-4 py-6 text-sm text-on-error-soft">Could not load your reports.</p>
      )}
      {!isLoading && !isError && reports.length === 0 && (
        <p className="px-4 py-6 text-sm text-ink-faint">
          Nothing yet — anything you send us shows up here with its status.
        </p>
      )}
      {reports.map((r) => {
        const status = STATUS_COPY[r.status_label] ?? STATUS_COPY.received
        return (
          <div
            key={r.id}
            className="border-b border-border-subtle last:border-b-0 hover:bg-surface-secondary transition-colors"
          >
            <button
              type="button"
              onClick={() => {
                if (r.conversation_id) {
                  onClose()
                  navigate(`/messaging?conversation=${r.conversation_id}`)
                }
              }}
              className="w-full text-left px-4 py-2.5"
            >
              <div className="flex items-center gap-2">
                {r.unread && (
                  <span
                    title="Support replied — unread"
                    className="w-2 h-2 rounded-full bg-claro-600 shrink-0"
                  />
                )}
                <span className="text-xs font-mono text-ink-faint shrink-0">{r.reference}</span>
                <span className="text-sm text-ink truncate flex-1">{r.message || '—'}</span>
                <span
                  className={cn(
                    'text-[11px] px-1.5 py-0.5 rounded font-medium shrink-0',
                    status.tone,
                  )}
                >
                  {status.label}
                </span>
              </div>
            </button>
            {r.confirmable && (
              <div className="flex items-center gap-2 px-4 pb-2.5 -mt-1">
                <span className="text-xs text-ink-tertiary">Did this sort it?</span>
                <button
                  type="button"
                  onClick={() => answer(r.id, true)}
                  className="text-xs px-2 py-0.5 rounded bg-success-light text-on-success-soft font-medium hover:bg-success/20"
                >
                  Yes
                </button>
                <button
                  type="button"
                  title="Reopens the case for the team"
                  onClick={() => answer(r.id, false)}
                  className="text-xs px-2 py-0.5 rounded bg-surface-tertiary text-ink-secondary font-medium hover:bg-border-subtle"
                >
                  Not quite
                </button>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function formatPageLabel(route: string, params: Record<string, string | undefined>): string {
  const keys = Object.keys(params).filter((k) => params[k])
  if (keys.length === 0) return route
  const ctx = keys
    .map((k) => `${k.replace(/_/g, ' ')}: ${truncate(String(params[k]), 20)}`)
    .join(' · ')
  return `${route} · ${ctx}`
}

function truncate(s: string, n: number): string {
  if (s.length <= n) return s
  return s.slice(0, n - 1) + '…'
}
